import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Check, X, ArrowLeft } from 'lucide-react';
import { CartItem } from './CartModal';
import { CustomTipModal } from './payment/CustomTipModal';
import { MenuItemsList } from './payment/MenuItemsList';
import { OrderSummary } from './payment/OrderSummary';
import { PaymentMethodsSection } from './payment/PaymentMethodsSection';
import { BloominCardInputForm } from './payment/BloominCardInputForm';
import { AddCardModal } from './payment/AddCardModal';
import { CardNicknameModal } from './payment/CardNicknameModal';
import { DeleteCardConfirmModal } from './payment/DeleteCardConfirmModal';
import { bloominPaymentTranslations, checkoutTranslations, paymentTranslations, savedCardsTranslations } from '@/i18n';
import type { BloominCardRegistrationPayload, SavedCard } from '@/lib/api/types';
import { usePaymentCalculations, useSavedCards } from '@/hooks';
import { useStoreAcceptedPaymentMethods } from '@/hooks/useStoreAcceptedPaymentMethods';
import { usePaymentApi } from '@/hooks/usePaymentApi';
import { useEstimatedPoints } from '@/hooks/useEstimatedPoints';
import { orderApi } from '@/lib/api/orders';
import { useApp } from '@/contexts/AppContext';
import { useAuth } from '@/contexts/AuthContext';
import { useCart } from '@/contexts/CartContext';
import { usePaymentFlow } from '@/contexts/CheckoutContext';
import { useStorePricing } from '@/contexts/StorePricingContext';
import { pointsApi } from '@/lib/api/points';
import { ApiClientError } from '@/lib/api/client';
import { isTrustedBloominChallengeUrl } from '@/lib/payments/bloominChallenge';
import { logger } from '@/utils/logger';
import { cartItemsToOrderItems, extractStoreId } from '@/utils/cartToOrder';
import { DEFAULT_STORE_TIMEZONE } from '@/utils/menuAvailability';
import { clampPointsForPayment, subtractMoney } from '@/utils/moneyMinor';
import { computeCartSignature } from '@/lib/orphan-order/storage';
import { PointsSection } from './checkout/PointsSection';

interface PaymentPageProps {
  isOpen: boolean;
  onClose: () => void;
  onPaymentSuccess: (paymentDetails: PaymentResult) => void;
  cartItems: CartItem[];
  totalAmount: number;
  tableNumber?: string;
  orderNumber?: string;
  mode?: 'pickup' | 'table';
}

export interface PaymentResult {
  orderId: string;
  paymentMethod: string;
  amount: number;
  timestamp: Date;
  status: 'success' | 'failed';
  // 결제 플로우 제어용 (선택적)
  showPaymentProcessing?: boolean;
  paymentFlow?: string;
  // 추가 데이터
  items?: CartItem[];
  grossSubtotal?: number;
  netSubtotal?: number;
  tax?: number;
  tip?: number;
  pointsUsed?: number;
  pointsDiscount?: number;
  total?: number;
  tableNumber?: string;
  // 유연한 확장 지원
  [key: string]: unknown;
}

function requirePaymentTranslation(value: string | undefined, key: string): string {
  if (value === undefined) {
    throw new Error(`Missing payment translation: ${key}`);
  }

  return value;
}

export function PaymentModal({
  isOpen,
  onClose,
  onPaymentSuccess,
  cartItems,
  totalAmount: _totalAmount,
  tableNumber: propTableNumber,
  orderNumber: propOrderNumber,
  mode = 'pickup'
}: PaymentPageProps) {
  const { locale, currency } = useApp();
  const { isLoggedIn } = useAuth();
  const { clearCart } = useCart();
  const { orderData } = usePaymentFlow();
  const { pointsEnabled } = useStorePricing();
  const modalRef = useRef<HTMLDivElement>(null);
  const [currentStep, setCurrentStep] = useState<'form' | 'processing' | 'bloomin-card' | 'success' | 'failed'>('form');
  const [selectedTip, setSelectedTip] = useState<number>(0);
  const [customTip, setCustomTip] = useState<string>('');
  const [showCustomTipModal, setShowCustomTipModal] = useState<boolean>(false);
  const [customTipMode, setCustomTipMode] = useState<'percentage' | 'amount'>('percentage');
  const [pointsToUse, setPointsToUse] = useState<number>(0);
  const [blockedServerTotal, setBlockedServerTotal] = useState<{ scope: string; total: number } | null>(null);
  const [useAllPoints, _setUseAllPoints] = useState<boolean>(false);
  const [selectedCard, setSelectedCard] = useState<number | null>(null); // #302: 카드 ID는 number 타입
  const [paymentResult, setPaymentResult] = useState<PaymentResult | null>(null);
  const [isPaymentMethodsExpanded, setIsPaymentMethodsExpanded] = useState<boolean>(false);

  // #302: 저장된 카드 관리 - useSavedCards 훅 사용
  const {
    cards: savedCards,
    isLoading: isCardsLoading,
    checkout,
    isCreatingCheckout,
    isConfirming,
    createCheckout,
    confirmBloominRegistration,
    updateNickname,
    setDefault,
    deleteCard,
  } = useSavedCards({ autoFetch: isOpen && isLoggedIn });

  // #207: 결제 API 연동
  const {
    createPayment,
    isLoading: isPaymentLoading,
    clearError: clearPaymentApiError,
  } = usePaymentApi();
  const [orderError, setOrderError] = useState<string | null>(null);
  const [bloominInitiateUrl, setBloominInitiateUrl] = useState<string | null>(null);
  const [createdOrderId, setCreatedOrderId] = useState<number | null>(null);
  const [createdPaymentId, setCreatedPaymentId] = useState<number | null>(null);

  // #302: 카드 관리 모달 상태
  const [showAddCardModal, setShowAddCardModal] = useState<boolean>(false);
  const [editingCard, setEditingCard] = useState<SavedCard | null>(null);
  const [deletingCard, setDeletingCard] = useState<SavedCard | null>(null);
  const [isUpdatingNickname, setIsUpdatingNickname] = useState<boolean>(false);
  const [isDeletingCard, setIsDeletingCard] = useState<boolean>(false);
  const [paymentMethodError, setPaymentMethodError] = useState<string | null>(null);

  // Issue #99: 포인트 API 연동
  const [totalPoints, setTotalPoints] = useState<number>(0);
  const [isPointsLoading, setIsPointsLoading] = useState<boolean>(false);
  const [usableAmount, setUsableAmount] = useState<number>(0);
  const [minUsePoints, setMinUsePoints] = useState<number>(0);
  const [pointsError, setPointsError] = useState<string | null>(null);

  const t = paymentTranslations[locale];
  const pointsLoadErrorLabel = requirePaymentTranslation(t.pointsLoadError, 'pointsLoadError');
  // #522 — Bloomin 카드 폼 라벨 15언어 전수 정의 → narrowing 없이 locale 직접 인덱싱.
  const bloominT = bloominPaymentTranslations[locale];

  // #212: API에서 받은 usableAmount 기반 사용 가능 포인트
  const availablePoints = usableAmount;
  const tableNumber = propTableNumber || '01';
  const orderNumber = propOrderNumber || '-';
  const currentDate = new Date();
  // storeId 메모이제이션: cartItems 수량 변경 시 불필요한 API 재호출 방지
  const storeIdForPoints = useMemo(() => extractStoreId(cartItems), [cartItems]);
  const { platformCardPrepayAvailability } = useStoreAcceptedPaymentMethods(isOpen ? storeIdForPoints : null);
  // The server resolves pickup card requests to platform prepay; table card requests are ambiguous.
  const platformPrepayUnavailable = mode === 'pickup' && platformCardPrepayAvailability === 'unavailable';

  // 결제 계산 훅 사용
  const {
    formatCurrency,
    formatDate,
    formatTime,
    calculateSubtotal,
    calculateTax,
    calculateTipAmount,
    calculateFinalTotal,
  } = usePaymentCalculations({
    currency,
    cartItems,
    selectedTip,
    customTip,
    customTipMode,
    useAllPoints,
    pointsToUse,
    availablePoints,
  });
  const payableTotal = calculateSubtotal() + calculateTipAmount();
  const paymentScope = JSON.stringify([
    storeIdForPoints, mode, propTableNumber, computeCartSignature(cartItems), calculateTipAmount(),
  ]);
  const safePointsToUse = isLoggedIn ? clampPointsForPayment(
    pointsToUse,
    usableAmount,
    payableTotal,
    minUsePoints,
  ) : 0;
  const safeFinalTotal = subtractMoney(payableTotal, safePointsToUse);
  const isPointsFullCover = safePointsToUse > 0 && safeFinalTotal === 0;

  // 팁·정책 변경으로 결제 총액이 낮아지면 표시값과 API 전송값을 같은 상한으로 맞춘다.
  useEffect(() => {
    if (pointsToUse !== safePointsToUse) {
      setPointsToUse(safePointsToUse);
    }
  }, [pointsToUse, safePointsToUse]);

  const grossAmount = calculateSubtotal();
  const tipAmount = calculateTipAmount();
  const { data: estimatedPointsData, isLoading: isEstimatingPoints } = useEstimatedPoints(
    storeIdForPoints ?? undefined,
    grossAmount,
    tipAmount,
    pointsToUse,
    isOpen && isLoggedIn && pointsEnabled,
  );

  // Handle body scroll for full page
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !orderData?.bloominInitiateUrl) {
      return;
    }

    setCreatedOrderId(typeof orderData.orderDbId === 'number' ? orderData.orderDbId : null);
    setCreatedPaymentId(typeof orderData.paymentId === 'number' ? orderData.paymentId : null);
    setBloominInitiateUrl(orderData.bloominInitiateUrl);
    setCurrentStep('bloomin-card');
  }, [isOpen, orderData]);

  // #302: 선택된 카드가 삭제되었을 때 자동 재선택
  useEffect(() => {
    if (selectedCard && savedCards.length > 0) {
      const selectedCardExists = savedCards.some(c => c.id === selectedCard);
      if (!selectedCardExists) {
        // 삭제된 카드가 선택된 카드였다면 기본 카드 또는 첫 번째 사용 가능한 카드로 자동 재선택
        const usableCards = savedCards.filter(c => c.is_usable);
        const newCard = usableCards.find(c => c.is_default) || usableCards[0];
        setSelectedCard(newCard?.id || null);
      }
    }
  }, [savedCards, selectedCard]);

  // #212: 매장별 사용 가능 포인트 조회 (getStoreAvailablePoints API)
  // #285 G3b: 매장 포인트 비활성 시 호출 자체 차단
  useEffect(() => {
    // 비로그인·모달 닫힘·매장 포인트 비활성 시 API 호출 스킵
    if (!isOpen || !isLoggedIn || !pointsEnabled) {
      setTotalPoints(0);
      setUsableAmount(0);
      setMinUsePoints(0);
      setPointsToUse(0);
      setPointsError(null);
      _setUseAllPoints(false);
      return;
    }

    if (!storeIdForPoints) {
      setTotalPoints(0);
      setUsableAmount(0);
      setMinUsePoints(0);
      setPointsToUse(0); // #2: storeId 없을 때 포인트 초기화
      setPointsError(null);
      _setUseAllPoints(false);
      return;
    }

    let cancelled = false;

    const fetchPoints = async () => {
      setIsPointsLoading(true);
      setPointsError(null);
      try {
        const response = await pointsApi.getStoreAvailablePoints(storeIdForPoints);
        if (cancelled) return;

        if (response.success && response.data) {
          setTotalPoints(Math.max(0, response.data.total_available));
          setUsableAmount(Math.max(0, response.data.usable_amount));
          setMinUsePoints(Math.max(0, response.data.min_use_points));
        } else {
          logger.warn('[PaymentModal] 포인트 API 실패 응답:', {
            success: response.success === true,
            hasData: response.data != null,
          });
          setTotalPoints(0);
          setUsableAmount(0);
          setMinUsePoints(0);
          setPointsToUse(0);
          setPointsError(pointsLoadErrorLabel);
        }
      } catch (err) {
        if (cancelled) return;

        // 401: 로그인 사용자의 세션 만료 가능성 — ApiClient의 onUnauthorized가 처리
        if (err instanceof ApiClientError && err.status === 401) {
          logger.warn('[PaymentModal] 포인트 조회 중 인증 만료 (401)');
          setTotalPoints(0);
          setUsableAmount(0);
          setMinUsePoints(0);
          setPointsToUse(0);
          setPointsError(pointsLoadErrorLabel);
          return;
        }

        // 다른 에러는 로깅만 하고 포인트 0으로 표시
        logger.error('[PaymentModal] 포인트 조회 실패:', err);
        setTotalPoints(0);
        setUsableAmount(0);
        setMinUsePoints(0);
        setPointsToUse(0);
        setPointsError(pointsLoadErrorLabel);
      } finally {
        if (!cancelled) {
          setIsPointsLoading(false);
        }
      }
    };

    fetchPoints();

    return () => {
      cancelled = true;
    };
  }, [isOpen, isLoggedIn, storeIdForPoints, pointsEnabled, pointsLoadErrorLabel]);

  const handleTipSelect = (percentage: number) => {
    // 같은 팁을 다시 클릭하면 선택 취소
    if (selectedTip === percentage) {
      setSelectedTip(0);
    } else {
      setSelectedTip(percentage);
    }
    setCustomTip('');
  };

  const _handleCustomTipChange = (value: string) => {
    setCustomTip(value);
    setSelectedTip(0);
  };

  // 커스텀 팁 버튼 클릭 핸들러
  const handleCustomTipButtonClick = () => {
    setShowCustomTipModal(true);
  };

  // 커스텀 팁 적용 핸들러 (CustomTipModal에서 호출)
  const handleApplyCustomTip = (tip: string, mode: 'percentage' | 'amount') => {
    setCustomTip(tip);
    setCustomTipMode(mode);
    setSelectedTip(0);
    setShowCustomTipModal(false);
  };

  // 커스텀 팁 모달 닫기 핸들러
  const handleCloseCustomTipModal = () => {
    setShowCustomTipModal(false);
  };

  const handleCardSelect = (cardId: number) => {
    setSelectedCard(cardId);
    // 카드 선택 시 에러 메시지 자동 제거
    setPaymentMethodError(null);
  };

  // #302: 새 카드 추가 모달 열기
  // 카드 저장(SavedCard)은 회원 전용(auth:customer) — 게스트가 진입하면 createCheckout 가 401 로
  // 입력폼 없이 'error' step 에 빠진다(#701). 게스트는 결제 진행 시 BloominCardInputForm 으로 카드 직접 입력.
  const handleOpenAddCardModal = useCallback(async () => {
    if (!isLoggedIn) return;
    setShowAddCardModal(true);
    setIsPaymentMethodsExpanded(false);
  }, [isLoggedIn]);

  // #302: 카드 등록 확인 (별명 포함)
  const handleConfirmBloominCardRegistration = useCallback(
    async (cardPayload: BloominCardRegistrationPayload, nickname?: string) => {
      await confirmBloominRegistration(cardPayload, nickname);
      setShowAddCardModal(false);
    },
    [confirmBloominRegistration]
  );

  // #302: 카드 별명 수정
  const handleUpdateNickname = useCallback(async (cardId: number, nickname: string | null) => {
    setIsUpdatingNickname(true);
    try {
      await updateNickname(cardId, nickname);
      setEditingCard(null);
    } finally {
      setIsUpdatingNickname(false);
    }
  }, [updateNickname]);

  // #302: 카드 삭제
  // 선택된 카드 자동 재선택은 useEffect에서 savedCards 변경 감지로 처리
  const handleDeleteCard = useCallback(async (cardId: number) => {
    setIsDeletingCard(true);
    try {
      await deleteCard(cardId);
      setDeletingCard(null);
    } finally {
      setIsDeletingCard(false);
    }
  }, [deleteCard]);

  // #302: 기본 카드 설정
  const handleSetDefault = useCallback(async (cardId: number) => {
    await setDefault(cardId);
  }, [setDefault]);

  // #145: 삭제 확인 모달 열기 (성능 최적화 - useCallback)
  const handleOpenDeleteCardModal = useCallback((cardId: number) => {
    const card = savedCards.find(c => c.id === cardId);
    if (card) setDeletingCard(card);
  }, [savedCards]);

  const handleClose = () => {
    if (currentStep === 'success') {
      if (paymentResult) {
        onPaymentSuccess(paymentResult);
      } else {
        onClose();
      }
    } else if (currentStep === 'failed') {
      setCurrentStep('form');
    } else if (currentStep === 'bloomin-card') {
      // 외부 PG 진행 단계에서는 form으로 복귀 + 생성된 리소스 정리
      setCurrentStep('form');
      setBloominInitiateUrl(null);
      setCreatedOrderId(null);
      setCreatedPaymentId(null);
    } else {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="min-h-screen bg-background text-foreground font-['Noto_Sans',sans-serif]">
      <div
        ref={modalRef}
        className="min-h-screen bg-background flex flex-col"
      >
        {/* Header */}
        <div className="bg-primary relative h-[50px] w-full shrink-0">
          {/* Back Button */}
          <button
            data-testid="payment-back"
            onClick={handleClose}
            className="absolute left-[20px] top-[15px] w-[12px] h-[21px]"
            disabled={currentStep === 'processing' || currentStep === 'bloomin-card'}
          >
            <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 12 21">
              <path d="M11.588 0.41098C11.4583 0.280705 11.3042 0.177346 11.1345 0.106823C10.9649 0.0363008 10.783 0 10.5993 0C10.4156 0 10.2337 0.0363008 10.0641 0.106823C9.89442 0.177346 9.74031 0.280705 9.61059 0.41098L0.326717 9.71087C0.223149 9.8144 0.140982 9.93738 0.0849197 10.0728C0.0288573 10.2082 0 10.3533 0 10.4999C0 10.6464 0.0288573 10.7916 0.0849197 10.9269C0.140982 11.0623 0.223149 11.1853 0.326717 11.2888L9.61059 20.5887C10.158 21.1371 11.0406 21.1371 11.588 20.5887C12.1354 20.0404 12.1354 19.1562 11.588 18.6079L3.49955 10.4943L11.5992 2.38063C12.1354 1.84345 12.1354 0.948157 11.588 0.41098Z" fill="white" />
            </svg>
          </button>
          
          {/* Title - Center */}
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
            <p className="font-['Noto_Sans:Bold',_'Noto_Sans_KR:Bold',_sans-serif] font-bold text-[18px] text-white tracking-[-0.36px] text-center uppercase whitespace-nowrap" style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}>
              {t.cardPayment}
            </p>
          </div>
        </div>



        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {currentStep === 'form' && (
            <div className="h-full overflow-y-auto scrollbar-hide">
              <div className="sm:p-4 lg:p-6 space-y-3 sm:space-y-4 lg:space-y-6 pb-[200px] max-h-[calc(100vh-120px)] overflow-y-auto scrollbar-hide pt-[0px] pr-[16px] pl-[16px]">
                {/* Order Info Header */}
                <div className="bg-background rounded-[5px] overflow-hidden">
                  <div className="flex flex-col py-3 px-4 bg-muted gap-2">
                    {/* Table Number or Order Number - One Line */}
                    <div className="flex items-baseline gap-2">
                      <span className="text-xs text-muted-foreground uppercase tracking-wide">
                        {mode === 'table' ? t.tableNumber : t.orderNumber}
                      </span>
                      <span className="font-bold text-foreground">
                        {mode === 'table' ? tableNumber : `#${orderNumber}`}
                      </span>
                    </div>
                    
                    {/* Date and Time - Left and Right */}
                    <div className="flex items-center justify-between gap-3 text-xs">
                      <span className="text-foreground-muted">
                        {formatDate(currentDate)}
                      </span>
                      <span className="text-foreground-muted">
                        {formatTime(currentDate)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Menu Items */}
                <MenuItemsList
                  cartItems={cartItems}
                  formatCurrency={formatCurrency}
                  t={{
                    menu: t.menu,
                    qty: t.qty,
                    price: t.price,
                  }}
                />

                {/* Tip Selection */}
                <div className="bg-background rounded-lg sm:rounded-xl p-3 sm:p-4 lg:p-6 shadow-sm">
                  <h3 className="font-medium mb-3 sm:mb-4 text-sm sm:text-base">{t.selectTip}</h3>
                  <div className="grid grid-cols-5 gap-2">
                    {[15, 20, 25, 30].map((percentage) => (
                      <button
                        key={percentage}
                        onClick={() => handleTipSelect(percentage)}
                        className={`h-[50px] flex items-center justify-center text-sm font-medium transition-all duration-200 border-2 rounded-[5px] active:scale-95 ${
                          selectedTip === percentage
                            ? 'bg-primary border-primary text-white shadow-sm'
                            : 'bg-background border-border text-foreground hover:border-primary hover:bg-primary/5'
                        }`}
                      >
                        {percentage}%
                      </button>
                    ))}
                    <button
                      onClick={handleCustomTipButtonClick}
                      className={`h-[50px] px-3 flex items-center justify-center text-sm font-medium transition-all duration-200 border-2 rounded-[5px] whitespace-nowrap active:scale-95 ${
                        customTip
                          ? 'bg-primary border-primary text-white shadow-sm'
                          : 'bg-background border-border text-foreground hover:border-primary hover:bg-primary/5'
                      }`}
                    >
                      {t.customInput}
                    </button>
                  </div>
                  
                  {/* 직접 입력한 팁 표시 */}
                  {customTip && !selectedTip && (
                    <div className="mt-3 p-3 bg-primary/10 rounded-lg border border-primary/30">
                      <p className="text-sm text-foreground text-center">
                        {customTipMode === 'percentage' ? `${customTip}%` : formatCurrency(parseFloat(customTip) || 0)}
                      </p>
                    </div>
                  )}
                </div>

                {/* Points Section */}
                {pointsEnabled && (
                  <div className="bg-background rounded-lg sm:rounded-xl p-3 sm:p-4 lg:p-6 shadow-sm">
                    <PointsSection
                      disabled={!isLoggedIn || isPointsLoading || totalPoints <= 0}
                      totalAvailable={totalPoints}
                      usableAmount={usableAmount}
                      minUsePoints={minUsePoints}
                      maxPoints={calculateSubtotal() + calculateTipAmount()}
                      onPointsChange={setPointsToUse}
                      pointsToUse={pointsToUse}
                      formatCurrency={formatCurrency}
                      loginPromptLabel={
                        !isLoggedIn
                          ? t.loginToSeePoints
                          : pointsError ?? undefined
                      }
                      labels={{
                        sectionTitle: t.pointsSection,
                        available: t.pointsAvailable,
                        usable: t.pointsUsable,
                        placeholder: t.pointsToUse,
                        apply: t.apply,
                        useAll: t.useAll,
                        discountApplied: t.pointsDiscountApplied,
                        minAmount: t.pointsMinAmount,
                      }}
                    />
                  </div>
                )}

                {/* Gift Card Section */}




                {/* Price Summary */}
                <OrderSummary
                  formatCurrency={formatCurrency}
                  calculateSubtotal={calculateSubtotal}
                  calculateTax={calculateTax}
                  calculateTipAmount={calculateTipAmount}
                  calculateFinalTotal={calculateFinalTotal}
                  selectedTip={selectedTip}
                  pointsToUse={pointsToUse}
                  estimatedPoints={estimatedPointsData?.estimatedPoints}
                  isEstimatingPoints={isEstimatingPoints}
                  t={{
                    orderSummary: t.orderSummary,
                    subtotal: t.subtotal,
                    netPrice: t.netPrice,
                    tax: t.tax,
                    tip: t.tip,
                    total: t.total,
                    pointsDiscount: t.pointsDiscount,
                    pointsDiscountApplied: t.pointsDiscountApplied,
                    estimatedPoints: t.estimatedPoints,
                    estimating: t.estimating,
                  }}
                />
              </div>

              {/* Fixed Bottom Section with Payment Methods and Button */}
              <div className="fixed bottom-0 left-0 right-0 bg-background border-t">
                {/* Payment Methods Section */}
                <PaymentMethodsSection
                  selectedCardId={selectedCard}
                  savedCards={savedCards}
                  isExpanded={isPaymentMethodsExpanded}
                  isLoading={isCardsLoading}
                  onToggleExpanded={() => setIsPaymentMethodsExpanded(!isPaymentMethodsExpanded)}
                  onCardSelect={(cardId) => {
                    handleCardSelect(cardId);
                    setIsPaymentMethodsExpanded(false);
                  }}
                  onAddNewCard={handleOpenAddCardModal}
                  showAddCard={isLoggedIn}
                  onEditCard={setEditingCard}
                  onDeleteCard={handleOpenDeleteCardModal}
                  onSetDefault={handleSetDefault}
                  t={{
                    paymentMethod: savedCardsTranslations[locale].paymentMethod,
                    selectCard: savedCardsTranslations[locale].selectCard,
                    registerNewCard: savedCardsTranslations[locale].registerNewCard,
                    defaultCard: savedCardsTranslations[locale].defaultCard,
                    expired: savedCardsTranslations[locale].expired,
                    loading: savedCardsTranslations[locale].loading,
                    noCards: savedCardsTranslations[locale].noCards,
                  }}
                />

                {/* Payment Error Message */}
                {paymentMethodError && (
                  <div className="px-4 pt-2">
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm text-red-600">{paymentMethodError}</p>
                    </div>
                  </div>
                )}

                {/* Payment Error Message (Order/Payment API) */}
                {orderError && (
                  <div className="px-4 pt-2">
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm text-red-600">{orderError}</p>
                    </div>
                  </div>
                )}

                {/* Payment Button */}
                <div className="p-4">
                  <Button
                    data-testid="payment-submit"
                    onClick={async () => {
                      if (!selectedCard && !isPointsFullCover) {
                        setPaymentMethodError(t.selectCardError);
                        return;
                      }
                      setPaymentMethodError(null);
                      setOrderError(null);
                      clearPaymentApiError();

                      // #207: 실제 결제 API 호출
                      try {
                        setCurrentStep('processing');

                        // 1. store_id 추출
                        const storeId = extractStoreId(cartItems);
                        if (!storeId) {
                          setOrderError(cartItems.length > 0 ? t.mixedStoreError : t.storeNotFound);
                          setCurrentStep('form');
                          return;
                        }

                        const knownTotal = blockedServerTotal?.scope === paymentScope
                          ? blockedServerTotal.total : payableTotal;
                        if (platformPrepayUnavailable && storeId === storeIdForPoints &&
                          subtractMoney(knownTotal, safePointsToUse) > 0) {
                          setOrderError(checkoutTranslations[locale].cardPaymentUnavailable);
                          setCurrentStep('form');
                          return;
                        }

                        // 2. CartItem → OrderItem 변환
                        const orderItems = cartItemsToOrderItems(cartItems);
                        if (orderItems.length === 0) {
                          setOrderError(t.noOrderableItems);
                          setCurrentStep('form');
                          return;
                        }

                        // 3. Order 생성
                        const tipAmount = calculateTipAmount();
                        const orderResponse = await orderApi.createOrder({
                          store_id: storeId,
                          order_type: mode === 'table' ? 'table' : 'pickup',
                          table_number: mode === 'table' ? propTableNumber : undefined,
                          items: orderItems,
                          tip: tipAmount > 0 ? tipAmount : undefined,
                        });

                        const orderId = orderResponse.data.id;
                        setCreatedOrderId(orderId);

                        if (platformPrepayUnavailable && storeId === storeIdForPoints &&
                          Number.isFinite(Number(orderResponse.data.total)) &&
                          subtractMoney(Number(orderResponse.data.total), safePointsToUse) > 0) {
                          setBlockedServerTotal({ scope: paymentScope, total: Number(orderResponse.data.total) });
                          setOrderError(checkoutTranslations[locale].cardPaymentUnavailable);
                          setCurrentStep('form');
                          return;
                        }

                        // 4. Payment 생성 (카드 결제) - #1: 포인트 결제 지원
                        const { payment, gateway, bloomin } = await createPayment(
                          orderId,
                          'card',
                          safePointsToUse,
                        );
                        setCreatedPaymentId(payment.id);

                        if ((payment.points_amount ?? 0) > 0 && Number(payment.amount) === 0) {
                          const completedPayment: PaymentResult = {
                            orderId: String(orderId),
                            paymentMethod: 'points',
                            amount: 0,
                            timestamp: new Date(),
                            status: 'success',
                            items: cartItems,
                            grossSubtotal: calculateSubtotal(),
                            tax: calculateTax(),
                            tip: tipAmount,
                            pointsUsed: safePointsToUse,
                            pointsDiscount: safePointsToUse,
                            total: 0,
                            tableNumber: mode === 'table' ? propTableNumber : undefined,
                          };
                          clearCart();
                          setPaymentResult(completedPayment);
                          setCurrentStep('success');
                          return;
                        }

                        if (gateway === 'bloomin' && bloomin?.initiate_url) {
                          setBloominInitiateUrl(bloomin.initiate_url);
                          setCurrentStep('bloomin-card');
                          return;
                        }

                        if (gateway !== 'bloomin' || !bloomin?.initiate_url) {
                          setOrderError('Bloomin 결제 시작 정보를 받지 못했습니다. 다시 시도해 주세요.');
                          setCurrentStep('form');
                          return;
                        }

                      } catch (err) {
                        logger.error('[PaymentModal] 결제 처리 실패:', err);
                        const message = err instanceof Error ? err.message : t.paymentFailedMessage;
                        setOrderError(message);
                        setCurrentStep('form');
                      }
                    }}
                    disabled={(!selectedCard && !isPointsFullCover) || isPaymentLoading}
                    className="w-full bg-primary hover:bg-primary-hover text-white h-12 font-semibold rounded-lg disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {isPaymentLoading
                      ? t.processing
                      : `${t.makePayment} • ${formatCurrency(safeFinalTotal)}`}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {currentStep === 'processing' && (
            <div className="flex flex-col items-center justify-center py-16 px-4 min-h-[60vh]">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-primary/20 border-t-primary mb-6"></div>
              <h3 className="text-xl font-semibold text-foreground mb-2">
                {t.processing}
              </h3>
              <p className="text-muted-foreground text-center">
                {t.processingMessage}
              </p>
            </div>
          )}

          {/* #1413: Bloomin 자체 카드 입력 + 3DS redirect 단계 */}
          {currentStep === 'bloomin-card' && bloominInitiateUrl && createdOrderId && createdPaymentId && (
            <div className="flex flex-col min-h-[60vh]">
              <div className="p-4 border-b">
                <button
                  onClick={() => {
                    setCurrentStep('form');
                    setBloominInitiateUrl(null);
                    setCreatedOrderId(null);
                    setCreatedPaymentId(null);
                  }}
                  className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-3"
                >
                  <ArrowLeft className="w-4 h-4" />
                  {bloominT.goBack}
                </button>
                <h3 className="text-lg font-semibold text-foreground">
                  {bloominT.title}
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {bloominT.description}
                </p>
              </div>

              <div className="flex-1 p-4">
                <BloominCardInputForm
                  initiateUrl={bloominInitiateUrl}
                  guestPassword={orderData?.guestPassword}
                  labels={bloominT.cardInput}
                  onSuccess={(result) => {
                    let challengeUrl: URL;
                    try {
                      challengeUrl = new URL(result.challengeUrl);
                    } catch {
                      logger.error('[PaymentModal] Bloomin challenge URL parse failed');
                      setOrderError(bloominT.invalidChallengeUrl);
                      setCurrentStep('form');
                      return;
                    }

                    if (!isTrustedBloominChallengeUrl(challengeUrl)) {
                      logger.error('[PaymentModal] Bloomin challenge URL blocked: untrusted host');
                      setOrderError(bloominT.invalidChallengeUrl);
                      setCurrentStep('form');
                      return;
                    }

                    logger.info('[PaymentModal] Bloomin 3DS redirect start:', {
                      paymentId: result.payment_id,
                      transaccionId: result.transaccionId,
                    });
                    window.location.assign(challengeUrl.toString());
                  }}
                  onError={(error) => {
                    setOrderError(error);
                    setCurrentStep('form');
                  }}
                />
              </div>

              <div className="p-4 border-t bg-muted">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">
                    {bloominT.amountToPay}
                  </span>
                  <span className="text-lg font-bold text-primary">
                    {formatCurrency(safeFinalTotal)}
                  </span>
                </div>
              </div>
            </div>
          )}

          {currentStep === 'success' && paymentResult && (
            <div className="flex flex-col items-center justify-center py-16 px-4 min-h-[60vh]">
              <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mb-6">
                <Check className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-6">
                {t.paymentSuccess}
              </h3>
              <Card className="w-full bg-gray-50 p-4 space-y-3 max-w-md">
                <div className="flex justify-between">
                  <span className="text-gray-600">{t.orderNumber}</span>
                  <span className="font-medium">{paymentResult.orderId}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t.paymentMethod}</span>
                  <span className="font-medium">{paymentResult.paymentMethod}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t.amount}</span>
                  <span className="font-medium text-primary">{formatCurrency(paymentResult.amount)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t.date}</span>
                  <span className="font-medium">
                    {paymentResult.timestamp.toLocaleString(undefined, { timeZone: DEFAULT_STORE_TIMEZONE })}
                  </span>
                </div>
              </Card>
              <Button
                onClick={handleClose}
                className="w-full bg-primary hover:bg-primary-hover text-white h-12 text-lg font-semibold rounded-lg mt-6 max-w-md"
              >
                {t.closeAndContinue}
              </Button>
            </div>
          )}

          {currentStep === 'failed' && (
            <div className="flex flex-col items-center justify-center py-16 px-4 min-h-[60vh]">
              <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mb-6">
                <X className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-4">
                {t.paymentFailed}
              </h3>
              <p className="text-muted-foreground text-center mb-8">
                {t.paymentFailedMessage}
              </p>
              <div className="flex gap-3 w-full max-w-md">
                <Button
                  onClick={handleClose}
                  variant="outline"
                  className="flex-1 h-12 font-semibold"
                >
                  {t.closeAndContinue}
                </Button>
                <Button
                  onClick={() => setCurrentStep('form')}
                  className="flex-1 bg-primary hover:bg-primary-hover text-white h-12 font-semibold"
                >
                  {t.tryAgain}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Custom Tip Modal */}
      <CustomTipModal
        isOpen={showCustomTipModal}
        onClose={handleCloseCustomTipModal}
        onApply={handleApplyCustomTip}
        initialTip={customTip}
        initialMode={customTipMode}
        formatCurrency={formatCurrency}
        calculateSubtotal={calculateSubtotal}
        t={{
          enterTip: t.enterTip,
          percentage: t.percentage,
          amount: t.amount,
          enterTipPercentage: t.enterTipPercentage,
          enterTipAmount: t.enterTipAmount,
          estimatedTip: t.estimatedTip,
          cancel: t.cancel,
          apply: t.apply
        }}
      />

      {/* #302: 카드 추가 모달 */}
      <AddCardModal
        isOpen={showAddCardModal}
        checkout={checkout}
        isCreatingCheckout={isCreatingCheckout}
        isConfirming={isConfirming}
        onClose={() => setShowAddCardModal(false)}
        onCreateCheckout={createCheckout}
        onConfirmBloominRegistration={handleConfirmBloominCardRegistration}
        t={{
          title: savedCardsTranslations[locale].addCardTitle,
          subtitle: savedCardsTranslations[locale].addCardSubtitle,
          loading: savedCardsTranslations[locale].loading,
          creatingCheckout: savedCardsTranslations[locale].creatingCheckout,
          confirmingRegistration: savedCardsTranslations[locale].confirmingRegistration,
          successTitle: savedCardsTranslations[locale].successTitle,
          successMessage: savedCardsTranslations[locale].successMessage,
          errorTitle: savedCardsTranslations[locale].errorTitle,
          errorMessage: savedCardsTranslations[locale].errorMessage,
          retry: savedCardsTranslations[locale].retry,
          done: savedCardsTranslations[locale].done,
          cancel: savedCardsTranslations[locale].cancel,
          nicknameLabel: savedCardsTranslations[locale].nicknameLabel,
          nicknamePlaceholder: savedCardsTranslations[locale].nicknamePlaceholder,
          skipNickname: savedCardsTranslations[locale].skipNickname,
          saveWithNickname: savedCardsTranslations[locale].saveWithNickname,
          cardNumberLabel: savedCardsTranslations[locale].cardNumberLabel,
          expiryMonthLabel: savedCardsTranslations[locale].expiryMonthLabel,
          expiryYearLabel: savedCardsTranslations[locale].expiryYearLabel,
          cvvLabel: savedCardsTranslations[locale].cvvLabel,
          holderLabel: savedCardsTranslations[locale].holderLabel,
          invalidCard: savedCardsTranslations[locale].invalidCard,
          invalidExpiry: savedCardsTranslations[locale].invalidExpiry,
          invalidCvv: savedCardsTranslations[locale].invalidCvv,
          invalidHolder: savedCardsTranslations[locale].invalidHolder,
        }}
      />

      {/* #302: 카드 별명 수정 모달 */}
      <CardNicknameModal
        isOpen={!!editingCard}
        card={editingCard}
        isSaving={isUpdatingNickname}
        onClose={() => setEditingCard(null)}
        onSave={handleUpdateNickname}
        t={{
          title: savedCardsTranslations[locale].editNickname,
          subtitle: savedCardsTranslations[locale].editNicknameSubtitle,
          placeholder: savedCardsTranslations[locale].nicknamePlaceholder,
          clearNickname: savedCardsTranslations[locale].clearNickname,
          cancel: savedCardsTranslations[locale].cancel,
          save: savedCardsTranslations[locale].save,
          saving: savedCardsTranslations[locale].saving,
          saveError: savedCardsTranslations[locale].saveError,
        }}
      />

      {/* #302: 카드 삭제 확인 모달 */}
      <DeleteCardConfirmModal
        isOpen={!!deletingCard}
        card={deletingCard}
        isDeleting={isDeletingCard}
        onClose={() => setDeletingCard(null)}
        onConfirm={handleDeleteCard}
        t={{
          title: savedCardsTranslations[locale].deleteCardTitle,
          message: savedCardsTranslations[locale].deleteCardMessage,
          defaultCardWarning: savedCardsTranslations[locale].deleteCardDefaultWarning,
          cancel: savedCardsTranslations[locale].cancel,
          delete: savedCardsTranslations[locale].delete,
          deleting: savedCardsTranslations[locale].deleting,
        }}
      />
    </div>
  );
}
